import { useCallback, useEffect, useRef, useState } from 'react';
import { runtimeEnv } from '../config/runtimeEnv';
import type { BotProtectionAction } from '../types/botProtection';

type TurnstileWidgetId = string;

type TurnstileRenderOptions = {
  sitekey: string;
  action: BotProtectionAction;
  size: 'normal' | 'flexible' | 'compact';
  callback: (token: string) => void;
  'error-callback': (errorCode?: string) => void;
  'expired-callback': () => void;
  'timeout-callback': () => void;
  'unsupported-callback': () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => TurnstileWidgetId;
  reset: (widgetId: TurnstileWidgetId) => void;
  remove: (widgetId: TurnstileWidgetId) => void;
};

type TrustedScriptUrlValue = object;

type TrustedScriptUrlPolicy = {
  createScriptURL: (url: string) => TrustedScriptUrlValue;
};

type TrustedTypesPolicyFactory = {
  createPolicy: (
    name: string,
    policyOptions: {
      createScriptURL: (url: string) => string;
    },
  ) => TrustedScriptUrlPolicy;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
    trustedTypes?: TrustedTypesPolicyFactory;
  }
}

type PendingChallenge = {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
  timeoutId: number;
};

const TURNSTILE_SCRIPT_ID = 'turnstile-script';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_TRUSTED_TYPES_POLICY = 'turnstile-script-loader';
const TURNSTILE_TIMEOUT_MS = 15_000;
const BOT_PROTECTION_UNAVAILABLE = 'BOT_PROTECTION_UNAVAILABLE';
const BOT_PROTECTION_FAILED = 'BOT_PROTECTION_FAILED';
const BOT_PROTECTION_REQUIRED = 'BOT_PROTECTION_REQUIRED';

let turnstileScriptUrlPolicy: TrustedScriptUrlPolicy | null = null;
let turnstileScriptPromise: Promise<void> | null = null;

function validateTurnstileScriptUrl(url: string) {
  const scriptUrl = new URL(url);
  const isTurnstileApiUrl = scriptUrl.origin === 'https://challenges.cloudflare.com'
    && scriptUrl.pathname === '/turnstile/v0/api.js';

  if (!isTurnstileApiUrl) {
    throw new Error(BOT_PROTECTION_UNAVAILABLE);
  }

  return scriptUrl.toString();
}

function getTurnstileScriptUrlPolicy() {
  if (!window.trustedTypes) {
    return null;
  }

  if (!turnstileScriptUrlPolicy) {
    turnstileScriptUrlPolicy = window.trustedTypes.createPolicy(TURNSTILE_TRUSTED_TYPES_POLICY, {
      createScriptURL: validateTurnstileScriptUrl,
    });
  }

  return turnstileScriptUrlPolicy;
}

function createTurnstileScriptUrl() {
  const policy = getTurnstileScriptUrlPolicy();

  if (!policy) {
    return TURNSTILE_SCRIPT_URL;
  }

  return policy.createScriptURL(TURNSTILE_SCRIPT_URL);
}

function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve();
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error(BOT_PROTECTION_UNAVAILABLE)), TURNSTILE_TIMEOUT_MS);
    const loaded = () => { window.clearTimeout(timeoutId); resolve(); };
    const failed = () => { window.clearTimeout(timeoutId); reject(new Error(BOT_PROTECTION_UNAVAILABLE)); };
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener('load', loaded, { once: true });
      existingScript.addEventListener('error', failed, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = createTurnstileScriptUrl() as string;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', loaded, { once: true });
    script.addEventListener('error', failed, { once: true });
    document.head.appendChild(script);
  }).catch((error) => {
    turnstileScriptPromise = null;
    document.getElementById(TURNSTILE_SCRIPT_ID)?.remove();
    throw error;
  });

  return turnstileScriptPromise;
}

function readBotProtectionErrorCode(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  return BOT_PROTECTION_FAILED;
}

type BotProtectionOptions = {
  size?: TurnstileRenderOptions['size'];
  requireSiteKey?: boolean;
  deferLoading?: boolean;
};

export function useBotProtection(action: BotProtectionAction, options: BotProtectionOptions = {}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<TurnstileWidgetId | null>(null);
  const pendingChallengeRef = useRef<PendingChallenge | null>(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const siteKey = runtimeEnv.VITE_TURNSTILE_SITE_KEY;
  const { size = 'normal', requireSiteKey = false, deferLoading = false } = options;

  const clearPendingChallenge = useCallback(() => {
    const pendingChallenge = pendingChallengeRef.current;
    if (!pendingChallenge) {
      return;
    }

    window.clearTimeout(pendingChallenge.timeoutId);
    pendingChallengeRef.current = null;
  }, []);

  const removeWidget = useCallback(() => {
    clearPendingChallenge();
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }
    setCaptchaToken('');
    setErrorCode('');
  }, [clearPendingChallenge]);

  const renderWidget = useCallback(() => {
    if (!siteKey || !containerRef.current || !window.turnstile || widgetIdRef.current) {
      return;
    }

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action,
        size,
        callback: (token: string) => {
          setErrorCode('');
          setCaptchaToken(token);
          const pendingChallenge = pendingChallengeRef.current;
          if (!pendingChallenge) {
            return;
          }

          clearPendingChallenge();
          pendingChallenge.resolve(token);
        },
        'error-callback': (nextErrorCode?: string) => {
          setCaptchaToken('');
          setErrorCode(nextErrorCode || BOT_PROTECTION_FAILED);
          const pendingChallenge = pendingChallengeRef.current;
          if (!pendingChallenge) {
            return;
          }

          clearPendingChallenge();
          pendingChallenge.reject(new Error(nextErrorCode || BOT_PROTECTION_FAILED));
        },
        'expired-callback': () => {
          setCaptchaToken('');
          setErrorCode(BOT_PROTECTION_REQUIRED);
          const pendingChallenge = pendingChallengeRef.current;
          if (!pendingChallenge) {
            return;
          }

          clearPendingChallenge();
          pendingChallenge.reject(new Error(BOT_PROTECTION_REQUIRED));
        },
        'timeout-callback': () => {
          setCaptchaToken('');
          setErrorCode(BOT_PROTECTION_REQUIRED);
          const pendingChallenge = pendingChallengeRef.current;
          if (!pendingChallenge) {
            return;
          }

          clearPendingChallenge();
          pendingChallenge.reject(new Error(BOT_PROTECTION_REQUIRED));
        },
        'unsupported-callback': () => {
          setCaptchaToken('');
          setErrorCode(BOT_PROTECTION_UNAVAILABLE);
          const pendingChallenge = pendingChallengeRef.current;
          if (!pendingChallenge) {
            return;
          }

          clearPendingChallenge();
          pendingChallenge.reject(new Error(BOT_PROTECTION_UNAVAILABLE));
        },
      });
    } catch (error) {
      setErrorCode(readBotProtectionErrorCode(error));
    }
  }, [action, clearPendingChallenge, siteKey, size]);

  const prepareCaptcha = useCallback(() => {
    if (!siteKey) return;
    void loadTurnstileScript()
      .then(renderWidget)
      .catch((error) => setErrorCode(readBotProtectionErrorCode(error)));
  }, [renderWidget, siteKey]);

  const captchaContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;

    if (!siteKey) {
      return;
    }

    if (!node) {
      removeWidget();
      return;
    }

    if (!deferLoading) prepareCaptcha();
  }, [deferLoading, prepareCaptcha, removeWidget, siteKey]);

  useEffect(() => {
    if (!siteKey) {
      return undefined;
    }

    return removeWidget;
  }, [removeWidget, siteKey]);

  const getCaptchaToken = useCallback(async () => {
    if (!siteKey) {
      if (requireSiteKey) {
        setErrorCode(BOT_PROTECTION_UNAVAILABLE);
        throw new Error(BOT_PROTECTION_UNAVAILABLE);
      }
      return undefined;
    }

    if (captchaToken) {
      return captchaToken;
    }

    setErrorCode('');
    await loadTurnstileScript();
    renderWidget();

    if (!widgetIdRef.current || !window.turnstile) {
      setErrorCode(BOT_PROTECTION_UNAVAILABLE);
      throw new Error(BOT_PROTECTION_UNAVAILABLE);
    }

    clearPendingChallenge();

    return new Promise<string>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        pendingChallengeRef.current = null;
        setErrorCode(BOT_PROTECTION_REQUIRED);
        reject(new Error(BOT_PROTECTION_REQUIRED));
      }, TURNSTILE_TIMEOUT_MS);

      pendingChallengeRef.current = { resolve, reject, timeoutId };
    });
  }, [captchaToken, clearPendingChallenge, renderWidget, requireSiteKey, siteKey]);

  const resetCaptcha = useCallback(() => {
    clearPendingChallenge();
    setCaptchaToken('');
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, [clearPendingChallenge]);

  return {
    captchaContainerRef,
    errorCode,
    hasError: Boolean(errorCode),
    isEnabled: Boolean(siteKey),
    isVerified: (!siteKey && !requireSiteKey) || Boolean(captchaToken),
    prepareCaptcha,
    getCaptchaToken,
    resetCaptcha,
  };
}
